import Phaser from 'phaser';
import Hero from '~/src/game/Hero';

import { createGhostAnims } from '~/src/game/GhostAnims';
import { createHeroAnims } from '~/src/game/HeroAnims';

import '~/src/game/Ghost';
import '~/src/game/Hero';

import { DEBUG_TIMESCALE, FRUIT_SPAWN, GHOST_POINTS, GHOST_SCARE_TIMER, levels, GODMODE, START_LIVES, START_TIMER } from '~/src/consts/config';
import { spriteSize, tileSize } from '~/src/consts/sizes';
import { EVENT_GAME_INITED, EVENT_GAME_LEVELUP, EVENT_GAME_START, EVENT_PACMAN_GAINLIFE, EVENT_PACMAN_HASPOWER, EVENT_PACMAN_LOSEPOWER, sceneEvents } from '~/src/events/GameEvents';
import { handlePlayerEatDot, handlePlayerEatFruit, handlePlayerEatPowerDot } from '~/src/game/colliders/heroDot';
import { handleHeroGhostCollision } from '~/src/game/colliders/heroGhost';
import { processRadiusCollision } from '~/src/game/colliders/radiusCollision';
import { green, orange, pink, red, white } from '~/src/game/colors';
import Ghost from '~/src/game/Ghost';
import ChaseHeroAI from '~/src/game/ghost-ai/ChaseHeroAI';
import FlankHeroAI from '~/src/game/ghost-ai/FlankHeroAI';
import IdleAI from '~/src/game/ghost-ai/IdleAI';
import InterceptHeroAI from '~/src/game/ghost-ai/InterceptHeroAI';
import PlayfullyChaseHeroAI from '~/src/game/ghost-ai/PlayfullyChaseHeroAI';
import ScatterAI from '~/src/game/ghost-ai/ScatterAI';
import { Moves } from '~/src/game/grid/grid';

export default class Game extends Phaser.Scene {
	public static SceneName = 'GameScene';
	private pad!: Phaser.Input.Gamepad.Gamepad;

	private _hero!: Hero;
	private boardLayer!: Phaser.Tilemaps.TilemapLayer;

	private cursors!: any;

	private blinky!: Ghost;
	private inky!: Ghost;
	private pinky!: Ghost;
	private clyde!: Ghost;

	private ghosts!: Ghost[];

	public _level = 0;
	private _maxDots = 0;
	private _winConditions = false;
	private _pointsPerGhost = GHOST_POINTS;
	public lives = START_LIVES;
	public _eatenDots: string[] = [];
	public _eatenEnergizers: string[] = [];
	private _energizerTimer?: Phaser.Time.TimerEvent;
	private _fruits: Phaser.GameObjects.Sprite[] = [];
	private door!: Phaser.GameObjects.Rectangle;

	constructor() {
		super(Game.SceneName);
	}

	preload() {
		this.load.tilemapTiledJSON('tilemap', 'levels/level-3.json');
		this.load.tilemapTiledJSON('kebab', 'levels/kebab.json');
	}

	create({ init, level = 0 }) {
		const isKebab = false;//= level % 2 === 1;
		this._level = level;
		this._winConditions = false;

		this.pad = this.input.gamepad.pad1;
		this.cursors = this.input.keyboard.createCursorKeys();

		if (init === true) {
			this._eatenDots = [];
			this._eatenEnergizers = [];
			this._pointsPerGhost = GHOST_POINTS;
			if (level === 0) {
				this.lives = START_LIVES;
			}
		}
		this._maxDots = isKebab ? 222 : 240;

		const map = this.make.tilemap({ key: isKebab ? 'kebab' : 'tilemap' });
		const tileset = map.addTilesetImage('map_tiles', 'tiles');
		this.boardLayer = map.createLayer('Board', tileset)
			.setCollisionByProperty({ collides: true })
			.forEachTile(t => {
				if (!t.properties.door) {
					t.tint = green.color;
				}
			});

		this.setupBoardObjects(map.getObjectLayer('BoardObjects'));
		this.setupDots(map.createLayer('Dots', tileset));
		this.setupSceneEvents();

		if (this.game.config.physics.arcade?.debug) {
			this.physics.world.timeScale = DEBUG_TIMESCALE;
			this.drawGrid();
		}
		this.scene.pause();
		sceneEvents.emit(EVENT_GAME_INITED);
	}

	update() {
		if (this._winConditions) {
			return;
		}
		if (this.hero && this.boardLayer && !this.physics.world.isPaused) {
			this.hero.handleMovement(this.pad, this.cursors, this.boardLayer, this.door);
		}

		if (this._eatenDots.length === this._maxDots && this._eatenEnergizers.length === 4) {
			this._winConditions = true;
			this.pauseGameWorld();
			const nextLevel = this._level + 1;
			sceneEvents.emit(EVENT_GAME_LEVELUP, nextLevel);
			this.time.delayedCall(START_TIMER, () => this.scene.restart({ init: true, level: nextLevel }));
		}

		FRUIT_SPAWN.forEach((numDots, i) => {
			if (this._eatenDots.length === numDots && !this._fruits[i].active) {
				this._fruits[i]
					.setVisible(true)
					.setActive(true);
			}
		});
	}

	private setupSceneEvents() {
		const gameStart = () => {
			this.scene.resume(Game.SceneName, {});
		};

		const hasPower = () => {
			this.ghosts.forEach(ghost => ghost.scare({ time: GHOST_SCARE_TIMER, numBlinks: 4 }));

			this._energizerTimer?.remove();
			this._energizerTimer = this.time.addEvent({
				delay: GHOST_SCARE_TIMER,
				callback: () => {
					sceneEvents.emit(EVENT_PACMAN_LOSEPOWER);
					this.ghosts.forEach(ghost => ghost.unscare());
					this.pointsPerGhost = GHOST_POINTS;
				},
				loop: false,
				paused: false
			});
		};

		const gainLife = () => {
			this.lives = this.lives + 1;
			sceneEvents.off(EVENT_PACMAN_GAINLIFE, gainLife);
		};

		sceneEvents.on(EVENT_GAME_START, gameStart);
		sceneEvents.on(EVENT_PACMAN_HASPOWER, hasPower);
		sceneEvents.on(EVENT_PACMAN_GAINLIFE, gainLife);

		this.events.once(Phaser.Scenes.Events.DESTROY, () => {
			sceneEvents.off(EVENT_GAME_START, gameStart);
			sceneEvents.off(EVENT_PACMAN_HASPOWER, hasPower);
			sceneEvents.off(EVENT_PACMAN_GAINLIFE, gainLife);
		});
	}

	private setupDots(dotsLayer: Phaser.Tilemaps.TilemapLayer) {
		const dots = dotsLayer.createFromTiles(39, -1, { key: 'kebab_tiles', frame: 'dot.png', origin: 0.5 }, this);
		dots.forEach((dot, i) => {
			const dotIndex = `${i}`;
			if (this._eatenDots.includes(dotIndex)) {
				dot.destroy();
				return;
			}

			dot.setPosition(dot.x + tileSize, dot.y + tileSize);
			dot.setDepth(1);
			dot.setTint(pink.color);
			dot.setName(dotIndex);

			this.physics.add.existing(dot, true);
			(dot.body as Phaser.Physics.Arcade.Body).debugShowBody = false;
		});

		const energizers = dotsLayer.createFromTiles(40, -1, { key: 'kebab_tiles', frame: 'energizer.png', origin: 0.5 }, this);
		energizers.forEach((energizer, i) => {
			const energizerIndex = `${i}`;
			if (this._eatenEnergizers.includes(energizerIndex)) {
				energizer.destroy();
				return;
			}
			energizer.setPosition(energizer.x + tileSize, energizer.y + tileSize);
			energizer.setDepth(1);
			energizer.setName(energizerIndex);

			this.physics.add.existing(energizer);
			(energizer.body as Phaser.Physics.Arcade.Body).debugShowBody = false;

			this.tweens.add({
				targets: energizer,
				alpha: 0,
				duration: 1000,
				yoyo: true,
				repeat: -1
			});
		});

		const fruit = levels[Math.min(this._level, 12)];
		this._fruits = [fruit, fruit].map(f => {
			const fruit = this.add.sprite(0, 0, 'kebab_tiles', `${f}.png`)
				.setName(f)
				.setPosition(28 * tileSize, 39 * tileSize)
				.setDepth(1)
				.setVisible(false)
				.setActive(false);
			this.physics.add.existing(fruit);
			return fruit;
		});

		if (this.hero) {
			this.physics.add.collider(this.hero, dots, handlePlayerEatDot, processRadiusCollision(tileSize), this);
			this.physics.add.collider(this.hero, energizers, handlePlayerEatPowerDot, processRadiusCollision(tileSize), this);
			this.physics.add.collider(this.hero, this._fruits, handlePlayerEatFruit, processRadiusCollision(spriteSize / 2), this);
		} else {
			throw new Error('Hero must be set up before the boardLayer (dots!)');
		}
	}

	private setupBoardObjects(objectLayer: Phaser.Tilemaps.ObjectLayer) {
		createHeroAnims(this.anims);
		createGhostAnims(this.anims);
		const objects = objectLayer.objects.reduce<{ [x: string]: Phaser.Types.Tilemaps.TiledObject }>(
			(obs, o) => ({ ...obs, [o.name]: o }), {});

		const hero = objects['spawn'];
		if (!hero) {
			throw new Error("No 'spawn' object in Board layer");
		} else {
			this.createHero(hero);
		}

		const door = objects['door'];
		this.door = this.add.rectangle(door.x, door.y, door.width, door.height);
		(this.physics.world.enableBody(this.door, Phaser.Physics.Arcade.STATIC_BODY)
			.body as Phaser.Physics.Arcade.Body).setOffset(this.door.width / 2, this.door.height / 2);

		const ghostObjects = [objects['blinky'], objects['inky'], objects['pinky'], objects['clyde']];
		this.createGhosts(ghostObjects);
	}

	private createGhosts(ghosts: Phaser.Types.Tilemaps.TiledObject[]) {
		const pos = ghosts.map(g => ({
			x: g.x! + spriteSize / 2,
			y: g.y! + spriteSize / 2
		}));

		this.blinky = this.add.ghost(pos[0], red, this.boardLayer, this.door, this._level);
		this.blinky.defaultAI = new ChaseHeroAI(this.hero!, this.blinky);
		this.blinky.scareAI = new ScatterAI(54, 5, this.blinky);

		this.inky = this.add.ghost(pos[1], green, this.boardLayer, this.door, this._level);
		this.inky.defaultAI = new FlankHeroAI(this.hero!, this.inky, this.blinky);
		this.inky.currentAI = new IdleAI(this.inky, Moves.Down);
		this.inky.scareAI = new ScatterAI(54, 64, this.inky);

		this.pinky = this.add.ghost(pos[2], pink, this.boardLayer, this.door, this._level);
		this.pinky.defaultAI = new InterceptHeroAI(this.hero!, this.pinky);
		this.pinky.currentAI = new IdleAI(this.pinky, Moves.Up);
		this.pinky.scareAI = new ScatterAI(1, 5, this.pinky);

		this.clyde = this.add.ghost(pos[3], orange, this.boardLayer, this.door, this._level);
		this.clyde.defaultAI = new PlayfullyChaseHeroAI(
			this.hero!,
			this.clyde,
		);
		this.clyde.currentAI = new IdleAI(this.clyde, Moves.Down);
		this.clyde.scareAI = new ScatterAI(1, 64, this.clyde);

		this.ghosts = [this.blinky, this.inky, this.pinky, this.clyde];
		if (!GODMODE) {
			this.physics.add.collider(
				this.hero!,
				this.ghosts,
				handleHeroGhostCollision,
				processRadiusCollision(spriteSize * 3 / 4),
				this
			);
		}
	}

	private createHero(hero: Phaser.Types.Tilemaps.TiledObject) {
		const heroCenter = {
			x: hero.x! + hero.width! / 2,
			y: hero.y! + hero.height! / 2
		};
		this.hero = this.add.hero(heroCenter, 'kebab_tiles', this._level);
		this.hero.setDepth(2);
		this.physics.add.collider(this.hero, this.boardLayer!);
	}

	private drawGrid() {
		for (let i = 0; i < this.boardLayer?.width; i += tileSize) {
			this.add.line(i, 0, undefined, undefined, 0, this.boardLayer?.height, white.color, 0.4)
				.setLineWidth(0.5)
				.setDepth(1001)
				.setOrigin(0);
		}
		for (let i = 0; i < this.boardLayer?.height; i += tileSize) {
			this.add.line(0, i, undefined, undefined, this.boardLayer?.width, 0, white.color, 0.4)
				.setLineWidth(0.5)
				.setDepth(1001)
				.setOrigin(0);
		}
	}

	public pauseGameWorld() {
		this.physics.world.pause();
	}

	get hero() {
		return this._hero;
	}
	set hero(newHero: Hero) {
		this._hero = newHero;
	}

	get pointsPerGhost() {
		return this._pointsPerGhost;
	}
	set pointsPerGhost(points: number) {
		this._pointsPerGhost = points;
	}

}
